import type { LocalViewpoint } from '@viewer/lib/viewpointsStore'
import type { BcfExtensions, BcfTopic } from './bcfParser'
import { elementosDoTopico, type ElementoLigado } from './bcfElementos'

/**
 * Agrupar a lista de tópicos de um BCF, sem nada à volta.
 *
 * Os critérios são os campos do tópico (prioridade, estado, responsável,
 * etiqueta, tipo) e o GRUPO: a etiqueta `Grupo <id>` que a exportação dos
 * conflitos põe em cada conflito de um grupo e, para os tópicos sem essa
 * etiqueta (BCF de outras plataformas), as componentes ligadas dos
 * RelatedTopics. Os tópicos sem valor ficam num bloco "Sem ..." no fim.
 *
 * Puro: recebe a lista já filtrada e ordenada (a ordem dentro de cada
 * bloco é essa) e devolve os blocos. A interface está em
 * `BcfTopicsView.tsx`. O critério escolhido é uma preferência do browser
 * (`lerAgrupar` / `gravarAgrupar`), não trabalho: a recuperação de sessão
 * não lhe toca.
 */

export type AgruparPor = 'nenhum' | 'grupo' | 'prioridade' | 'estado' | 'responsavel' | 'etiqueta' | 'tipo'

export const OPCOES_AGRUPAR: { valor: AgruparPor; rotulo: string }[] = [
  { valor: 'nenhum', rotulo: 'Nenhum' },
  { valor: 'grupo', rotulo: 'Grupo' },
  { valor: 'prioridade', rotulo: 'Prioridade' },
  { valor: 'estado', rotulo: 'Estado' },
  { valor: 'responsavel', rotulo: 'Responsável' },
  { valor: 'etiqueta', rotulo: 'Etiqueta' },
  { valor: 'tipo', rotulo: 'Tipo' },
]

export function isAgruparPor(v: unknown): v is AgruparPor {
  return OPCOES_AGRUPAR.some((o) => o.valor === v)
}

/** Chave da preferência em localStorage. Não é trabalho (ver
 *  `isWorkKey` em core/session/recoveryManifest.ts). */
export const CHAVE_AGRUPAR = 'vn2r.bcf.agrupar'

/** O critério guardado neste browser; 'nenhum' sem preferência ou sem
 *  armazenamento. */
export function lerAgrupar(): AgruparPor {
  try {
    const v = localStorage.getItem(CHAVE_AGRUPAR)
    return isAgruparPor(v) ? v : 'nenhum'
  } catch {
    return 'nenhum'
  }
}

/** Guarda o critério; sem armazenamento, fica só nesta sessão. */
export function gravarAgrupar(v: AgruparPor): void {
  try {
    if (v === 'nenhum') localStorage.removeItem(CHAVE_AGRUPAR)
    else localStorage.setItem(CHAVE_AGRUPAR, v)
  } catch {
    // Sem armazenamento: a escolha vale até fechar a página.
  }
}

/** Prefixo das etiquetas de grupo, o mesmo da exportação dos conflitos. */
export const PREFIXO_GRUPO = 'Grupo '

export interface BlocoDeTopicos {
  /** Chave estável do bloco (para dobrar e para o React). */
  chave: string
  /** O que o cabeçalho mostra. */
  nome: string
  /** Detalhe opcional (o id do grupo, quando o nome é o título). */
  detalhe: string | null
  topicos: BcfTopic[]
  /** O bloco "Sem ..." do fim. */
  semValor: boolean
  /** A prioridade mais alta dos tópicos do bloco, tal como está escrita
   *  no tópico, ou null. */
  prioridadeMaisAlta: string | null
  /** Quantos tópicos do bloco estão abertos (estado não terminal). */
  abertos: number
}

// --- Prioridade e estado ---------------------------------------------------------

/** Peso das prioridades conhecidas (maior é mais grave). Os catálogos
 *  não servem para ordenar: o de omissão do módulo vai de Low a Critical
 *  e o da exportação dos conflitos de Critical a Low. */
const PESO_PRIORIDADE: Record<string, number> = {
  critical: 4,
  'crítica': 4,
  critica: 4,
  urgent: 4,
  urgente: 4,
  high: 3,
  alta: 3,
  normal: 2,
  medium: 2,
  'média': 2,
  media: 2,
  low: 1,
  baixa: 1,
}

/** Peso de uma prioridade: as conhecidas de 1 a 4; as outras 0 (ficam
 *  depois das conhecidas, por ordem alfabética). */
export function pesoDaPrioridade(p: string | null | undefined): number {
  if (!p) return -1
  return PESO_PRIORIDADE[p.trim().toLowerCase()] ?? 0
}

/** A mais alta de uma lista de prioridades, tal como vem escrita. */
export function prioridadeMaisAlta(ps: readonly (string | null | undefined)[]): string | null {
  let melhor: string | null = null
  for (const p of ps) {
    if (!p) continue
    if (melhor === null || pesoDaPrioridade(p) > pesoDaPrioridade(melhor)) melhor = p
  }
  return melhor
}

/** O estado é terminal? Pelo catálogo do BCF (isTerminal) e, para os
 *  estados que lá não estão, pela mesma heurística do importador. */
export function estadoTerminal(status: string | null | undefined, ext?: BcfExtensions | null): boolean {
  if (!status) return false
  const lower = status.toLowerCase()
  const doCatalogo = ext?.statuses.find((s) => s.label.toLowerCase() === lower)
  if (doCatalogo) return doCatalogo.isTerminal
  return /clos|resolv|done|fechad/i.test(status)
}

// --- Grupo -------------------------------------------------------------------------

/** O id do grupo de um tópico, pela etiqueta `Grupo <id>` (a primeira,
 *  se houver várias). */
export function grupoDaEtiqueta(t: BcfTopic): string | null {
  for (const l of t.labels) {
    if (l.startsWith(PREFIXO_GRUPO)) {
      const id = l.slice(PREFIXO_GRUPO.length).trim()
      if (id) return id
    }
  }
  return null
}

/** A primeira linha da descrição, quando é `Grupo <id>: <título>` (com
 *  ou sem o "(conflito i de n)" no fim, que sai). */
export function tituloDaDescricao(t: BcfTopic): { id: string; titulo: string } | null {
  const primeira = (t.description ?? '').split(/\r?\n/, 1)[0]?.trim() ?? ''
  const m = /^Grupo (\S+?): (.+)$/.exec(primeira)
  if (!m) return null
  const titulo = m[2].replace(/\s*\(conflito \d+ de \d+\)\s*$/, '').trim()
  return titulo ? { id: m[1], titulo } : null
}

/** Union-find simples sobre índices. */
class Conjuntos {
  private pai: number[]
  constructor(n: number) {
    this.pai = Array.from({ length: n }, (_, i) => i)
  }
  raiz(i: number): number {
    let r = i
    while (this.pai[r] !== r) r = this.pai[r]
    // Compressão do caminho.
    let x = i
    while (this.pai[x] !== r) {
      const seguinte = this.pai[x]
      this.pai[x] = r
      x = seguinte
    }
    return r
  }
  unir(a: number, b: number): void {
    const ra = this.raiz(a)
    const rb = this.raiz(b)
    if (ra === rb) return
    // A raiz é sempre o índice menor: o representante não depende da
    // ordem das uniões.
    if (ra < rb) this.pai[rb] = ra
    else this.pai[ra] = rb
  }
}

/**
 * O grupo de cada tópico, por GUID.
 *
 * - Com etiqueta `Grupo <id>`: o grupo é esse.
 * - Sem etiqueta: as componentes ligadas dos RelatedTopics (nos dois
 *   sentidos), calculadas sobre TODOS os tópicos do BCF, para um filtro
 *   não partir um grupo ao meio. Se a componente tiver tópicos com
 *   etiqueta, fica no grupo da etiqueta do primeiro deles (pela ordem do
 *   BCF); senão é um grupo dos relacionados, com a chave do primeiro
 *   tópico da componente. Uma componente de um só tópico não é grupo.
 */
export function gruposDosTopicos(todos: readonly BcfTopic[]): Map<string, string> {
  const indice = new Map<string, number>()
  todos.forEach((t, i) => indice.set(t.guid, i))
  const uf = new Conjuntos(todos.length)
  todos.forEach((t, i) => {
    for (const g of t.relatedTopics ?? []) {
      const j = indice.get(g)
      if (j !== undefined && j !== i) uf.unir(i, j)
    }
  })
  const tamanho = new Map<number, number>()
  const etiquetaDaComponente = new Map<number, string>()
  todos.forEach((t, i) => {
    const r = uf.raiz(i)
    tamanho.set(r, (tamanho.get(r) ?? 0) + 1)
    const g = grupoDaEtiqueta(t)
    if (g && !etiquetaDaComponente.has(r)) etiquetaDaComponente.set(r, g)
  })
  const out = new Map<string, string>()
  todos.forEach((t, i) => {
    const g = grupoDaEtiqueta(t)
    if (g) {
      out.set(t.guid, `etiqueta:${g}`)
      return
    }
    const r = uf.raiz(i)
    if ((tamanho.get(r) ?? 1) < 2) return
    const daEtiqueta = etiquetaDaComponente.get(r)
    out.set(t.guid, daEtiqueta ? `etiqueta:${daEtiqueta}` : `relacionados:${todos[r].guid}`)
  })
  return out
}

// --- Blocos ------------------------------------------------------------------------

interface Opcoes {
  /** Todos os tópicos do BCF (para as componentes dos RelatedTopics). Sem
   *  eles, usam-se os da lista. */
  todos?: readonly BcfTopic[]
  extensions?: BcfExtensions | null
}

function ordemNoCatalogo(lista: readonly { label: string; sortOrder: number }[] | undefined, v: string): number {
  const e = lista?.find((x) => x.label.toLowerCase() === v.toLowerCase())
  return e ? e.sortOrder : Number.POSITIVE_INFINITY
}

const comparar = (a: string, b: string) => a.localeCompare(b, 'pt', { sensitivity: 'base', numeric: true })

const SEM: Record<Exclude<AgruparPor, 'nenhum'>, string> = {
  grupo: 'Sem grupo',
  prioridade: 'Sem prioridade',
  estado: 'Sem estado',
  responsavel: 'Sem responsável',
  etiqueta: 'Sem etiqueta',
  tipo: 'Sem tipo',
}

function bloco(
  chave: string,
  nome: string,
  topicos: BcfTopic[],
  semValor: boolean,
  ext: BcfExtensions | null | undefined,
  detalhe: string | null = null,
): BlocoDeTopicos {
  return {
    chave,
    nome,
    detalhe,
    topicos,
    semValor,
    prioridadeMaisAlta: prioridadeMaisAlta(topicos.map((t) => t.priority)),
    abertos: topicos.filter((t) => !estadoTerminal(t.status, ext)).length,
  }
}

/** Os valores de um tópico para o critério (vários na etiqueta). */
function valoresDe(t: BcfTopic, por: Exclude<AgruparPor, 'nenhum' | 'grupo'>): string[] {
  const um = (v: string | null | undefined) => (v && v.trim() ? [v.trim()] : [])
  switch (por) {
    case 'prioridade':
      return um(t.priority)
    case 'estado':
      return um(t.status)
    case 'responsavel':
      return um(t.assignedTo)
    case 'tipo':
      return um(t.type)
    case 'etiqueta':
      return [...new Set(t.labels.map((l) => l.trim()).filter(Boolean))]
  }
}

/**
 * Os blocos da lista para o critério. `nenhum` dá um só bloco com tudo.
 * Na etiqueta, um tópico com várias etiquetas aparece em cada uma.
 */
export function agruparTopicos(
  topicos: readonly BcfTopic[],
  por: AgruparPor,
  opcoes: Opcoes = {},
): BlocoDeTopicos[] {
  const ext = opcoes.extensions ?? null
  if (por === 'nenhum') return [bloco('todos', 'Todos', [...topicos], false, ext)]

  if (por === 'grupo') {
    const grupos = gruposDosTopicos(opcoes.todos ?? topicos)
    const porChave = new Map<string, BcfTopic[]>()
    const sem: BcfTopic[] = []
    for (const t of topicos) {
      const g = grupos.get(t.guid)
      if (!g) {
        sem.push(t)
        continue
      }
      const l = porChave.get(g)
      if (l) l.push(t)
      else porChave.set(g, [t])
    }
    const out: BlocoDeTopicos[] = []
    // Pela ordem do primeiro tópico de cada grupo na lista: o grupo segue
    // a ordenação escolhida.
    for (const [chave, ts] of porChave) {
      const id = chave.startsWith('etiqueta:') ? chave.slice('etiqueta:'.length) : null
      // O título vem da primeira linha da descrição de um tópico do grupo
      // ("Grupo X: título"); senão, o nome da etiqueta.
      const doTitulo = ts.map(tituloDaDescricao).find((x) => x !== null && (id === null || x.id === id)) ?? null
      let nome: string
      let detalhe: string | null
      if (id !== null) {
        // Desde 16/10/2026 a descrição já não traz a linha do grupo: o nome
        // passa a ser o título do primeiro tópico (os BCF antigos mantêm o
        // título do grupo, tirado da descrição).
        nome = doTitulo ? doTitulo.titulo : (ts[0]?.title ?? `${PREFIXO_GRUPO}${id}`)
        detalhe = `${PREFIXO_GRUPO}${id}`
      } else {
        nome = doTitulo ? doTitulo.titulo : `Relacionados com ${ts[0].index !== null ? `#${ts[0].index} ` : ''}${ts[0].title}`
        detalhe = 'Tópicos relacionados (RelatedTopics)'
      }
      out.push(bloco(`grupo:${chave}`, nome, ts, false, ext, detalhe))
    }
    if (sem.length > 0) out.push(bloco('grupo:sem', SEM.grupo, sem, true, ext))
    return out
  }

  const porValor = new Map<string, { nome: string; topicos: BcfTopic[] }>()
  const sem: BcfTopic[] = []
  for (const t of topicos) {
    const vs = valoresDe(t, por)
    if (vs.length === 0) {
      sem.push(t)
      continue
    }
    for (const v of vs) {
      // Sem diferenciar maiúsculas: "Open" e "open" são o mesmo bloco, com
      // a primeira grafia.
      const k = v.toLowerCase()
      const e = porValor.get(k)
      if (e) e.topicos.push(t)
      else porValor.set(k, { nome: v, topicos: [t] })
    }
  }
  const entradas = [...porValor.entries()]
  const ordenar = (a: [string, { nome: string }], b: [string, { nome: string }]): number => {
    const na = a[1].nome
    const nb = b[1].nome
    if (por === 'prioridade') return pesoDaPrioridade(nb) - pesoDaPrioridade(na) || comparar(na, nb)
    const lista =
      por === 'estado' ? ext?.statuses : por === 'tipo' ? ext?.types : por === 'etiqueta' ? ext?.labels : undefined
    if (lista) {
      const oa = ordemNoCatalogo(lista, na)
      const ob = ordemNoCatalogo(lista, nb)
      if (oa !== ob) return oa < ob ? -1 : 1
    }
    return comparar(na, nb)
  }
  entradas.sort(ordenar)
  const out = entradas.map(([k, e]) => bloco(`${por}:${k}`, e.nome, e.topicos, false, ext))
  if (sem.length > 0) out.push(bloco(`${por}:sem`, SEM[por], sem, true, ext))
  return out
}

/** Os elementos ligados dos tópicos de um bloco: a união dos elementos
 *  dos viewpoints anexados, sem repetidos, cada um com a sua cor (a do
 *  ficheiro, A/B nos conflitos, ou a da paleta). */
export function elementosDoBloco(
  topicos: readonly BcfTopic[],
  viewpoints: readonly Pick<LocalViewpoint, 'id' | 'viewpoint'>[],
): ElementoLigado[] {
  const porId = new Map(viewpoints.map((v) => [v.id, v]))
  const anexados: Pick<LocalViewpoint, 'id' | 'viewpoint'>[] = []
  const vistos = new Set<string>()
  for (const t of topicos) {
    for (const id of t.viewpointRefs) {
      const v = porId.get(id)
      if (!v || vistos.has(id)) continue
      vistos.add(id)
      anexados.push(v)
    }
  }
  return elementosDoTopico(anexados)
}

// --- Blocos grandes ----------------------------------------------------------------

/**
 * A partir de quantos elementos "Ver no 3D" e "Caixa de corte" de um
 * bloco pedem confirmação antes de avançar.
 *
 * O custo cresce com o número de elementos e com o número de modelos
 * abertos: a resolução faz, por elemento e por modelo aberto, um pedido
 * ao Fragments (GlobalId para localId) e, no modelo onde o encontra, a
 * lista dos itens com geometria e a leitura do Name. Com dois modelos são
 * cerca de quatro pedidos por elemento, em série: 300 elementos são 1 200
 * pedidos, 1 240 elementos quase 5 000 (contagem no teste do
 * `bcfElementosCena`, docs/CLASH.md, 10.10).
 */
export const LIMITE_ELEMENTOS_DO_BLOCO = 300

/** Um inteiro com espaço a separar os milhares ("1 240"). Determinístico:
 *  não depende da localização do browser. */
export function formatarInteiro(n: number): string {
  const s = String(Math.trunc(Math.abs(n)))
  const partes: string[] = []
  for (let i = s.length; i > 0; i -= 3) partes.unshift(s.slice(Math.max(0, i - 3), i))
  return `${n < 0 ? '-' : ''}${partes.join(' ')}`
}

/**
 * A pergunta a fazer antes de seleccionar os elementos de um bloco, ou
 * null quando o bloco não passa do limite (e aí avança sem perguntar).
 */
export function confirmacaoDoBloco(
  elementos: number,
  limite: number = LIMITE_ELEMENTOS_DO_BLOCO,
): string | null {
  if (elementos <= limite) return null
  return `Este bloco tem ${formatarInteiro(elementos)} elementos; a seleção pode demorar. Continuar?`
}

// --- O que a lista mostra ----------------------------------------------------------

/**
 * Os tópicos que a lista mostra: sem agrupar, os filtrados; agrupada, os
 * dos blocos abertos (não dobrados). É o que os pins sobre o modelo
 * seguem com "Seguir os filtros do módulo BCF".
 */
export function guidsVisiveisDaLista(
  filtrados: readonly Pick<BcfTopic, 'guid'>[],
  blocos: readonly Pick<BlocoDeTopicos, 'chave' | 'topicos'>[] | null,
  dobrados: ReadonlySet<string>,
): string[] {
  if (!blocos) return filtrados.map((t) => t.guid)
  const out = new Set<string>()
  for (const b of blocos) {
    if (dobrados.has(b.chave)) continue
    for (const t of b.topicos) out.add(t.guid)
  }
  return [...out]
}
